import { ensureToken, fetchJsonWithAuth } from './authApi';
import { AUTH_URL, JOBS_URL, CANDIDATES_URL, SUBMISSIONS_URL } from './apiConfig';

// ── Module Detail & Record Edit ───────────────────────────────────────────────

// fetchUrl — direct downstream (read-only, no tenant injection needed)
// updateUrl is no longer used; updates go through the Auth gateway.
const MODULE_EDIT_CONFIG = {
  job: {
    fetchUrl: (id) => `${JOBS_URL}/edit/detailed-view/${encodeURIComponent(id)}`,
  },
  candidates: {
    fetchUrl: (id) => `${CANDIDATES_URL}/edit/detailed-view?_id=${encodeURIComponent(id)}`,
  },
  submission: {
    fetchUrl: (id) => `${SUBMISSIONS_URL}/edit/detailed-view?_id=${encodeURIComponent(id)}`,
  },
};

export async function getModuleDataDetail(module, id) {
  const normalizedModule = String(module ?? '').trim().toLowerCase();

  if (normalizedModule === 'job' || normalizedModule === 'jobs') {
    const { getJobDetailView } = await import('./jobsApi');
    return getJobDetailView(id);
  }

  const params = new URLSearchParams({ module: String(module ?? ''), id: String(id) });
  return fetchJsonWithAuth(AUTH_URL, `/module-data-detail?${params.toString()}`);
}

export async function getRecordForEdit(module, id) {
  if (!module) throw new Error('module is required');
  if (!id) throw new Error('id is required');

  const config = MODULE_EDIT_CONFIG[module];
  if (!config) throw new Error(`No edit config for module: ${module}`);

  const token = await ensureToken();
  const res = await fetch(config.fetchUrl(id), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  const json = await res.json();
  return json.data ?? json;
}

export async function updateRecord(module, id, payload, fileParts = []) {
  if (!module) throw new Error('module is required');
  if (!id) throw new Error('id is required');

  const token = await ensureToken();
  const formData = new FormData();
  formData.append('json', JSON.stringify(payload));
  // New file uploads ride along in the SAME multipart, under the part name the
  // downstream update handler reads (jobs → "file", candidates → resume/passport/
  // documents/…). The gateway forwards them and the downstream stores + returns
  // their name/location. Existing files (no originFileObj) are not re-sent.
  (fileParts ?? []).forEach(({ formKey, file }) => formData.append(formKey, file));

  const res = await fetch(
    `${AUTH_URL}/module/update/${encodeURIComponent(id)}?module=${encodeURIComponent(module)}`,
    { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: formData },
  );

  if (!res.ok) {
    const errorText = await res.text();
    console.error('[updateRecord] Error response:', errorText);
    throw new Error(errorText || `API ${res.status}: ${res.statusText}`);
  }

  const json = await res.json();
  return json.data ?? json;
}

// ── Activity & Notes ──────────────────────────────────────────────────────────

const unwrap = (json) => json?.data ?? json;

export async function getActivity(module, id) {
  if (!module || !id) return [];
  const params = new URLSearchParams({ module, id: String(id) });
  const json = await fetchJsonWithAuth(AUTH_URL, `/module-activity?${params.toString()}`);
  const data = unwrap(json);
  return Array.isArray(data) ? data : [];
}

export async function getNotes(relatedId) {
  if (!relatedId) return [];
  const json = await fetchJsonWithAuth(AUTH_URL, `/notes?relatedId=${encodeURIComponent(relatedId)}`);
  const data = unwrap(json);
  return Array.isArray(data) ? data : [];
}

export async function createNote({ relatedId, notes, title, notesFor }) {
  const json = await fetchJsonWithAuth(AUTH_URL, '/notes', {
    method: 'POST',
    body: JSON.stringify({ relatedId, notes, title, notesFor }),
  });
  return unwrap(json);
}

export async function uploadJobAttachment(recordId, file) {
    const { ensureToken } = await import('./authApi');
    const token = await ensureToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('jobId', recordId);

    const res = await fetch(`${JOBS_URL}/jobs/${recordId}/attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
    });

    if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `API ${res.status}: ${res.statusText}`);
    }

    const json = await res.json();
    return json.data ?? json;
}